import {useCallback, useRef, useState} from "react";

import {DurationFormatOptions, durationSecFn} from "@/lib/format.tsx";
import {AnalyzerSource, AnalyzerTurnEnd} from "@/lib/queries/analyzerRuns.ts";
import {analyzerFetch} from "@/pages/analyzer/Analyzer.auth.ts";
import {
  AggregatedUsage,
  AnalyzerAuthError,
  AnalyzerPhase,
  SSEEvent,
  SSEEventType,
  SSERespondOptions,
  SSEStreamBody,
} from "@/pages/analyzer/Analyzer.types.ts";
import {addUsages, getTurnType, isInteractive, parseSSELine} from "@/pages/analyzer/Analyzer.utils.ts";
import {useAnalyzerRunTracking} from "@/pages/analyzer/hooks/useAnalyzerRunTracking.ts";

export const useSSEStream = () => {
  const tracking = useAnalyzerRunTracking();
  const [events, setEvents] = useState<SSEEvent[]>([]);
  const [phase, setPhase] = useState<AnalyzerPhase>(AnalyzerPhase.IDLE);
  const [error, setError] = useState<string | null>(null);
  const [usage, setUsage] = useState<AggregatedUsage | null>(null);
  const usageBaselineRef = useRef<AggregatedUsage | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const threadIdRef = useRef<string | null>(null);
  const lastStreamCallRef = useRef<{url: string; body: SSEStreamBody} | null>(null);
  const resumePhaseRef = useRef<AnalyzerPhase | null>(null);

  const abortOpenStream = useCallback(() => {
    if (abortControllerRef.current) {
      tracking.endTurn(AnalyzerTurnEnd.ABORTED);
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, [tracking]);

  const abort = useCallback(() => {
    abortOpenStream();
    setPhase((current) =>
      current === AnalyzerPhase.STREAMING ? (resumePhaseRef.current ?? AnalyzerPhase.ABORTED) : current
    );
  }, [abortOpenStream]);

  const failStream = useCallback((message: string) => {
    setError(message);
    setPhase((current) => (isInteractive(current) ? current : (resumePhaseRef.current ?? AnalyzerPhase.ERROR)));
  }, []);

  const processEvent = useCallback(
    (event: SSEEvent) => {
      tracking.onEvent(event);
      setEvents((prev) => [...prev, event]);

      if (event.usage && event.type !== SSEEventType.DONE) {
        setUsage(addUsages(usageBaselineRef.current, event.usage));
      }

      switch (event.type) {
        case SSEEventType.STARTED:
          threadIdRef.current = event.thread_id;
          break;
        case SSEEventType.INTERVIEW:
        case SSEEventType.CHECK_SELECTION: {
          const isInterview = event.type === SSEEventType.INTERVIEW;
          setPhase(isInterview ? AnalyzerPhase.INTERVIEW : AnalyzerPhase.CHECK_SELECTION);
          break;
        }
        case SSEEventType.ERROR:
          failStream(event.message);
          break;
        case SSEEventType.DONE:
          setPhase((current) => (isInteractive(current) ? current : AnalyzerPhase.DONE));
          usageBaselineRef.current = addUsages(usageBaselineRef.current, event.usage, event.elapsed);
          setUsage(usageBaselineRef.current);
          tracking.endTurn(AnalyzerTurnEnd.DONE);
          break;
      }
    },
    [failStream, tracking]
  );

  const startStream = useCallback(
    (url: string, body: SSEStreamBody, source?: AnalyzerSource) => {
      abortOpenStream();

      lastStreamCallRef.current = {url, body};
      const turnType = getTurnType(body);
      tracking.beginTurn(turnType, {url: body.url, source});
      const controller = new AbortController();
      abortControllerRef.current = controller;
      setPhase((current) => {
        resumePhaseRef.current = isInteractive(current) ? current : null;
        return AnalyzerPhase.STREAMING;
      });
      setError(null);

      (async () => {
        try {
          const response = await analyzerFetch(url, {
            method: "POST",
            headers: {"Content-Type": "application/json"},
            body: JSON.stringify(body),
            signal: controller.signal,
          });

          if (response.status === 429) {
            tracking.onRequestFailed(response.status);
            const retryAfterSeconds = Number(response.headers.get("Retry-After"));
            const retryMessage =
              retryAfterSeconds > 0
                ? `Please try again in ${durationSecFn(retryAfterSeconds, DurationFormatOptions.SHORT_TOP2)}`
                : "Please try again later";
            failStream(`You have reached your AI Analysis limit. ${retryMessage} or contact support.`);
            return;
          }

          if (!response.ok) {
            tracking.onRequestFailed(response.status);
            let errorMessage = `Request failed: ${response.status}`;
            const errorBody = await response.json().catch(() => null);
            const detail = errorBody?.detail;
            if (typeof detail === "string") {
              errorMessage = detail;
            } else if (Array.isArray(detail)) {
              errorMessage = detail.map((item: {msg?: string}) => item.msg || String(item)).join(", ");
            }
            failStream(errorMessage);
            return;
          }

          if (!response.body) {
            tracking.endTurn(AnalyzerTurnEnd.CONNECTION_LOST);
            failStream("No response body");
            return;
          }

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";

          while (true) {
            const {done, value} = await reader.read();
            if (done || controller.signal.aborted) break;

            buffer += decoder.decode(value, {stream: true});
            const lines = buffer.split("\n");
            buffer = lines.pop() ?? "";

            for (const line of lines) {
              if (controller.signal.aborted) break;
              const event = parseSSELine(line);
              if (event) {
                processEvent(event);
              }
            }
          }

          if (buffer.trim() && !controller.signal.aborted) {
            const event = parseSSELine(buffer);
            if (event) {
              processEvent(event);
            }
          }
          if (!controller.signal.aborted) {
            // fallback for streams cut before the `done` event; a no-op when `done` ended the turn
            tracking.endTurn(AnalyzerTurnEnd.DONE);
          }
        } catch (err) {
          if (err instanceof DOMException && err.name === "AbortError") {
            return;
          }
          tracking.endTurn(AnalyzerTurnEnd.CONNECTION_LOST);
          failStream(err instanceof AnalyzerAuthError ? err.message : "Connection lost");
        } finally {
          if (abortControllerRef.current === controller) {
            abortControllerRef.current = null;
          }
        }
      })();
    },
    [abortOpenStream, processEvent, failStream, tracking]
  );

  const reset = useCallback(() => {
    abortOpenStream();
    setEvents([]);
    setPhase(AnalyzerPhase.IDLE);
    threadIdRef.current = null;
    setError(null);
    setUsage(null);
    usageBaselineRef.current = null;
  }, [abortOpenStream]);

  const retry = useCallback(
    (respondUrl: string) => {
      const currentThreadId = threadIdRef.current;
      if (currentThreadId) {
        startStream(respondUrl, {thread_id: currentThreadId});
      } else if (lastStreamCallRef.current) {
        reset();
        startStream(lastStreamCallRef.current.url, lastStreamCallRef.current.body);
      }
    },
    [startStream, reset]
  );

  const respond = useCallback(
    (respondUrl: string, answer: string, options?: SSERespondOptions) => {
      const currentThreadId = threadIdRef.current;
      if (!currentThreadId) return;
      const {
        action,
        selectedIds,
        availableTags,
        availableContactGroups,
        currentTags,
        currentContactGroups,
        checkPositions,
        createdIds,
        checkpointId,
        subCheckpointId,
        subCheckpointNs,
      } = options ?? {};
      const body: SSEStreamBody = {
        thread_id: currentThreadId,
        answer,
        ...(action && {action}),
        ...(selectedIds && {selected_ids: selectedIds}),
        ...(availableTags && {available_tags: availableTags}),
        ...(availableContactGroups && {available_contact_groups: availableContactGroups}),
        ...(currentTags && {current_tags: currentTags}),
        ...(currentContactGroups && {current_contact_groups: currentContactGroups}),
        ...(checkPositions && {check_positions: checkPositions}),
        ...(createdIds && {created_ids: createdIds}),
        ...(checkpointId && {checkpoint_id: checkpointId}),
        ...(subCheckpointId && {sub_checkpoint_id: subCheckpointId, sub_checkpoint_ns: subCheckpointNs || ""}),
      };
      startStream(respondUrl, body);
    },
    [startStream]
  );

  const getThreadId = useCallback(() => threadIdRef.current, []);

  return {events, phase, error, usage, startStream, abort, retry, respond, reset, getThreadId};
};
